import { FunctionFragment } from 'ethers'

import registry from '../../consts/zkxClearSigning/registry.json'
import hexDescriptor from '../../consts/zkxClearSigning/descriptors/hex.json'
import uniswapV2RouterDescriptor from '../../consts/zkxClearSigning/descriptors/uniswap-v2-router.json'
import uniswapV3RouterDescriptor from '../../consts/zkxClearSigning/descriptors/uniswap-v3-router.json'
import uniswapV3Router02Descriptor from '../../consts/zkxClearSigning/descriptors/uniswap-v3-router02.json'
import { AccountOp } from '../accountOp/accountOp'
import { humanizeAccountOp } from '../humanizer'
import { resolveErc7730Descriptors } from '../humanizer/erc7730/registry'
import {
  Erc7730CallDescriptors,
  Erc7730Descriptor,
  Erc7730Known
} from '../humanizer/erc7730/types'
import { getRegistryKey } from '../humanizer/erc7730/utils'
import { HumanizerWarning, IrCall } from '../humanizer/interfaces'
import { getWarning } from '../humanizer/utils'

/**
 * ZKX clear signing: ERC-7730 descriptors for PulseChain contracts, bundled with the app instead of
 * fetched from the relayer (no relayer has descriptors for chain 369, and a bundled set never tells
 * a server which contract the user is about to call). Plan: extension/docs/clear-signing-plan.md.
 *
 * Kept outside `libs/humanizer` on purpose, so the next upstream humanizer port can replace that
 * folder without touching this one.
 */

// Kill-switch: `CLEAR_SIGNING_ENABLED="false"` in `.env` turns the bundled descriptors and the ZKX
// warnings off, leaving the plain humanizer. The value is babel-inlined at build time. It defaults to
// ON (display-only feature), and the `typeof` guard keeps an unset key from throwing in a service
// worker, where `process` does not exist once the expression is left un-inlined.
export const isZkxClearSigningEnabled = (): boolean =>
  typeof process === 'undefined' || process.env?.CLEAR_SIGNING_ENABLED !== 'false'

const ZKX_DESCRIPTORS_BY_PATH: Record<string, Erc7730Descriptor> = {
  'zkx/uniswap-v2-router.json': uniswapV2RouterDescriptor as Erc7730Descriptor,
  'zkx/uniswap-v3-router.json': uniswapV3RouterDescriptor as Erc7730Descriptor,
  'zkx/uniswap-v3-router02.json': uniswapV3Router02Descriptor as Erc7730Descriptor,
  'zkx/hex.json': hexDescriptor as Erc7730Descriptor
}

const ZKX_REGISTRY = registry as Record<string, { path: string }>

export const getZkxErc7730Known = (() => {
  let known: Erc7730Known | null = null

  return (): Erc7730Known => {
    if (!known) {
      known = {
        contractDescriptors: { ...ZKX_REGISTRY },
        descriptorsByPath: { ...ZKX_DESCRIPTORS_BY_PATH },
        eip712Descriptors: {},
        safeSingletons: {}
      }
    }
    return known
  }
})()

// The selectors each bundled descriptor declares a format for, by descriptor path.
const getOwnSelectors = (() => {
  const cache: Record<string, Set<string>> = {}

  return (path: string): Set<string> => {
    if (!cache[path]) {
      const formats = ZKX_DESCRIPTORS_BY_PATH[path]?.display?.formats || {}
      cache[path] = new Set(
        Object.keys(formats).map((formatKey) =>
          FunctionFragment.from(`function ${formatKey}`).selector.toLowerCase()
        )
      )
    }
    return cache[path]!
  }
})()

/**
 * The ERC-7730 descriptors to apply to an accountOp: only for calls to a contract in the ZKX
 * registry, with a function that contract's own descriptor declares.
 *
 * `resolveErc7730Descriptors` also returns the library's built-in descriptors for every ERC-20
 * `approve`/`transfer` and every `multicall`, on any chain and any contract. Those would change how
 * transfers and approvals look everywhere (and break the Activity heuristics that read the legacy
 * wording), so they are dropped here. HEX `approve`/`transfer` stay with the legacy Tokens module
 * the same way, since the HEX descriptor only declares its staking functions.
 */
export const getZkxErc7730Descriptors = (accountOp: AccountOp): Erc7730CallDescriptors => {
  const resolved = resolveErc7730Descriptors(accountOp, getZkxErc7730Known())
  const descriptors: Erc7730CallDescriptors = {}

  Object.entries(resolved).forEach(([indexKey, descriptor]) => {
    const index = Number(indexKey)
    const call = accountOp.calls[index]
    if (!call?.to || !call.data || call.data.length < 10) return

    const entry = ZKX_REGISTRY[getRegistryKey(accountOp.chainId, call.to)]
    if (!entry) return
    if (!getOwnSelectors(entry.path).has(call.data.slice(0, 10).toLowerCase())) return

    descriptors[index] = descriptor
  })

  return descriptors
}

export const ZKX_RECIPIENT_NOT_SELF_WARNING_CODE = 'ZKX_RECIPIENT_NOT_SELF'

const BENEFICIARY_PATHS = new Set(['to', 'params.recipient'])
// Uniswap V3 routers read these as "the caller" (MSG_SENDER = address(1)); address(2) keeps the
// output in the router for a later step of a multicall, which the bundled descriptors don't cover.
const MSG_SENDER_ALIAS = '0x0000000000000000000000000000000000000001'

/**
 * Warns when a call described by a ZKX descriptor sends its output to an address other than the
 * account that signs it (Rabby rule 1069: a swap paying out to someone else is the classic drainer
 * pattern). Only looks at calls that got a ZKX descriptor, so it never fires on other chains.
 */
export const getZkxRecipientWarnings = (accountOp: AccountOp, call: IrCall): HumanizerWarning[] => {
  const account = accountOp.accountAddr.toLowerCase()

  return (call.fullVisualization || []).flatMap((visualization) => {
    if (visualization.type !== 'erc7730') return []

    return visualization.fields.flatMap((row) => {
      if (row.type !== 'single-value' || !row.path || !BENEFICIARY_PATHS.has(row.path)) return []
      const recipient = row.value.address?.toLowerCase()
      if (!recipient || recipient === account || recipient === MSG_SENDER_ALIAS) return []

      return [
        getWarning(
          'The tokens from this transaction will be sent to another address, not to your account.',
          ZKX_RECIPIENT_NOT_SELF_WARNING_CODE,
          false,
          recipient
        )
      ]
    })
  })
}

/**
 * `humanizeAccountOp` with the ZKX clear-signing descriptors and warnings. Every place that shows
 * an accountOp to the user (the signing screen and the Activity history) calls this, so a past
 * transaction reads the same way it did while it was being signed.
 */
export const humanizeAccountOpWithZkxClearSigning = (
  accountOp: AccountOp,
  // Lets the library warn when a described call also sends native value the descriptor doesn't show
  options: { nativeAssetSymbol?: string } = {}
): IrCall[] => {
  if (!isZkxClearSigningEnabled()) return humanizeAccountOp(accountOp)

  let erc7730Descriptors: Erc7730CallDescriptors = {}
  try {
    erc7730Descriptors = getZkxErc7730Descriptors(accountOp)
  } catch (error) {
    // A bad descriptor must never block signing: show the plain humanization instead.
    console.error('ZKX clear signing: resolving descriptors failed', error)
  }
  if (!Object.keys(erc7730Descriptors).length) return humanizeAccountOp(accountOp)

  return humanizeAccountOp(accountOp, {
    erc7730Descriptors,
    nativeAssetSymbol: options.nativeAssetSymbol
  }).map((call, index) => {
    if (!erc7730Descriptors[index]) return call
    const warnings = getZkxRecipientWarnings(accountOp, call)

    return warnings.length ? { ...call, warnings: [...(call.warnings || []), ...warnings] } : call
  })
}
